package dev.lingvo.shared.screenshots.doc

import dev.lingvo.shared.screenshots.doc.DesignIssueCode.BAD_KEY
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.BAD_LOCALE
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.BAD_TEXT
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.OUT_OF_RANGE
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.UNKNOWN_LAYER
import dev.lingvo.shared.screenshots.doc.DesignIssueCode.UNSUPPORTED_VERSION
import dev.lingvo.shared.validateKeyName
import dev.lingvo.shared.validateLocale

/** Patch validation uses the target's real type and page scope; no reference can reach another device's layers. */
internal object OverrideValidation {
    fun validate(sink: IssueSink, page: Page, path: String, schemaVersion: Int) {
        if (page.overrides.isEmpty()) return
        if (page.overrides.size > DesignLimits.MAX_OVERRIDE_LOCALES) {
            sink.add(OUT_OF_RANGE, "$path/overrides", "At most ${DesignLimits.MAX_OVERRIDE_LOCALES} locales override a page; found ${page.overrides.size}.")
        }
        val layers = HashMap<String, Layer>()
        collect(page.layers, layers, depth = 0)
        val seen = HashSet<String>()
        for ((locale, override) in page.overrides) {
            val localePath = JsonPointer.child("$path/overrides", locale.take(64))
            val problem = validateLocale(locale)
            when {
                problem != null -> sink.add(BAD_LOCALE, localePath, problem)
                DesignValidator.canonicalLocale(locale) != locale -> sink.add(BAD_LOCALE, localePath,
                    "Write the locale as '${DesignValidator.canonicalLocale(locale)}'.")
            }
            if (!seen.add(locale.lowercase())) sink.add(BAD_LOCALE, localePath, "Locale '${locale.take(40)}' has two overrides on this page.")
            if (schemaVersion < 3 && !override.inheritLanguage) {
                sink.add(UNSUPPORTED_VERSION, "$localePath/inheritLanguage", "Language inheritance choices require design schema 3.")
            }
            if (override.patches.size > DesignLimits.MAX_PATCHES_PER_LOCALE) {
                sink.add(OUT_OF_RANGE, "$localePath/patches", "At most ${DesignLimits.MAX_PATCHES_PER_LOCALE} patches per locale; found ${override.patches.size}.")
            }
            for ((layerId, patch) in override.patches) {
                val patchPath = JsonPointer.child("$localePath/patches", layerId.take(64))
                val layer = layers[layerId]
                if (layer == null) sink.add(UNKNOWN_LAYER, patchPath, "No layer '${layerId.take(40)}' on this page.")
                if (schemaVersion >= 3 && layer != null && layer !is TextLayer && hasTextFields(patch)) {
                    sink.add(BAD_TEXT, patchPath, "Text properties require a text layer.")
                }
                patch(sink, patch, patchPath, page.slotIds.size, schemaVersion)
            }
        }
    }

    private fun collect(layers: List<Layer>, into: MutableMap<String, Layer>, depth: Int) {
        for (layer in layers) {
            into[layer.base.id] = layer
            if (layer is GroupLayer && depth < DesignLimits.MAX_GROUP_DEPTH) collect(layer.children, into, depth + 1)
        }
    }

    private fun hasTextFields(patch: LayerPatch): Boolean = patch.fit != null || patch.family != null || patch.weight != null ||
        patch.align != null || patch.lineHeight != null || patch.letterSpacingEm != null || patch.content != null

    private fun patch(sink: IssueSink, patch: LayerPatch, path: String, slots: Int, schemaVersion: Int) {
        patch.box?.let { sink.box("$path/box", it) }
        patch.fit?.let { sink.fit("$path/fit", it) }
        sink.font("$path/family", patch.family)
        patch.weight?.let { sink.weight("$path/weight", it) }
        patch.lineHeight?.let { sink.lineHeight("$path/lineHeight", it) }
        patch.letterSpacingEm?.let { sink.letterSpacing("$path/letterSpacingEm", it) }
        patch.content?.let { content ->
            if (schemaVersion < 3) sink.add(UNSUPPORTED_VERSION, "$path/content", "Caption binding overrides require design schema 3.")
            when (content) {
                is TextContent.Key -> validateKeyName(content.key)?.let { sink.add(BAD_KEY, "$path/content/key", it) }
                is TextContent.SlotCaption -> sink.slot("$path/content/slot", content.slot, slots)
                is TextContent.Literal -> sink.add(BAD_TEXT, "$path/content", "Use a caption key or slot binding; translated words belong in project strings.")
            }
        }
    }
}
